/**
 * CorrelationHeatmap — Plotly heatmap showing pairwise thesis-set correlations.
 * Shared by Portfolio page (post-purchase) and wizard Step 4 (pre-purchase).
 */
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import * as api from '../lib/api';

interface Props {
  matrix?: Record<string, Record<string, number>>;
  violations?: api.CorrelationViolation[];
  autoLoad?: boolean;
}

export function CorrelationHeatmap({ matrix: externalMatrix, violations: externalViolations, autoLoad = true }: Props) {
  const [matrix, setMatrix] = useState<Record<string, Record<string, number>>>(externalMatrix || {});
  const [violations, setViolations] = useState<api.CorrelationViolation[]>(externalViolations || []);
  const [loading, setLoading] = useState(!externalMatrix && autoLoad);

  useEffect(() => {
    if (externalMatrix) {
      setMatrix(externalMatrix);
      setViolations(externalViolations || []);
      return;
    }
    if (!autoLoad) return;
    setLoading(true);
    api.portfolio.correlations()
      .then((r) => {
        setMatrix(r.matrix);
        setViolations(r.violations);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [externalMatrix, autoLoad]);

  const symbols = Object.keys(matrix).sort();

  if (loading) return <div className="text-xs text-ink-mute">Loading correlation matrix...</div>;
  if (symbols.length < 2) {
    return (
      <div className="card p-4 text-xs text-ink-mute" data-testid="correlation-empty">
        Need at least 2 thesis sets to show correlations. Create more theses to see the matrix.
      </div>
    );
  }

  // AE-010 anti-hallucination Layer 4: a missing pairwise correlation is
  // NULL, not 0. (Correlation 0 = "uncorrelated" is a strong empirical
  // claim; missing data ≠ uncorrelated.) Plotly renders null cells as
  // empty so the gap is visible to the operator.
  const z: (number | null)[][] = symbols.map((a) =>
    symbols.map((b) => {
      const v = matrix[a]?.[b];
      return typeof v === 'number' ? v : null;
    }),
  );

  const annotations: Array<{
    x: string; y: string; text: string;
    showarrow: boolean; font: { color: string; size: number };
  }> = [];
  for (let i = 0; i < symbols.length; i++) {
    for (let j = 0; j < symbols.length; j++) {
      const val = z[i][j];
      annotations.push({
        x: symbols[j],
        y: symbols[i],
        text: val === null ? '—' : val.toFixed(2),
        showarrow: false,
        font: {
          color: val !== null && Math.abs(val) > 0.7 ? '#fff' : '#334155',
          size: 10,
        },
      });
    }
  }

  return (
    <div className="card p-4" data-testid="correlation-heatmap">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Cross-Thesis Correlation</h3>
        {violations.length > 0 && (
          <span className="text-xs text-bad font-semibold">
            {violations.length} violation{violations.length !== 1 ? 's' : ''} (&gt;0.70)
          </span>
        )}
      </div>
      <Plot
        data={[{
          z,
          x: symbols,
          y: symbols,
          type: 'heatmap',
          colorscale: [
            [0, '#dbeafe'],
            [0.5, '#f8fafc'],
            [0.7, '#fef3c7'],
            [1, '#ef4444'],
          ],
          zmin: -1,
          zmax: 1,
          showscale: true,
          colorbar: { title: { text: 'Corr' }, thickness: 15, len: 0.8 },
        }]}
        layout={{
          height: Math.max(250, symbols.length * 40 + 80),
          margin: { t: 10, b: 60, l: 70, r: 40 },
          xaxis: { tickangle: -45 },
          annotations,
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: { size: 11, color: '#64748b' },
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: '100%' }}
      />
      {violations.length > 0 && (
        <div className="mt-3 space-y-1">
          {violations.map((v, i) => (
            <div key={i} className="text-xs text-bad">
              {v.symbol_a} / {v.symbol_b}: {v.correlation.toFixed(2)} (exceeds 0.70 cap)
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
